import * as Tone from 'tone';

/**
 * Effects - Master effects chain
 * Input → Compressor → Saturation → [Reverb/Delay sends] → Limiter → Destination
 */
export class Effects {
  private reverb: Tone.Reverb;
  private delay: Tone.FeedbackDelay;
  private compressor: Tone.Compressor;
  private saturation: Tone.Distortion;
  private saturationMix: Tone.Gain;
  private saturationDry: Tone.Gain;
  private limiter: Tone.Limiter;

  // Pre-effects bus (dry signal after compression/saturation)
  private dryGain: Tone.Gain;

  // Effects sends
  private reverbSend: Tone.Gain;
  private delaySend: Tone.Gain;

  // Internal routing
  private postCompressor: Tone.Gain;
  private saturationWet: Tone.Gain;
  private saturationMerge: Tone.Gain;

  // Input node (all layers connect here)
  public input: Tone.Gain;

  constructor() {
    // Create input node
    this.input = new Tone.Gain(1.0);

    // Create compressor (dynamics control)
    this.compressor = new Tone.Compressor({
      threshold: -24, // Start compressing at -24dB
      ratio: 4, // 4:1 compression ratio
      attack: 0.003, // 3ms attack
      release: 0.25, // 250ms release
      knee: 10, // Soft knee for smoother compression
    });

    // Post-compressor gain (makeup gain)
    this.postCompressor = new Tone.Gain(1.0);

    // Create saturation (harmonic warmth/distortion)
    this.saturation = new Tone.Distortion({
      distortion: 0.2, // Subtle warmth by default
      wet: 1.0, // 100% wet for the saturation path
      oversample: '2x', // Reduce aliasing
    });

    // Saturation mix controls (dry/wet blend)
    this.saturationDry = new Tone.Gain(1.0); // Dry path
    this.saturationWet = new Tone.Gain(0.0); // Wet path (saturation off by default)
    this.saturationMerge = new Tone.Gain(1.0); // Merge point

    // Create dry signal path
    this.dryGain = new Tone.Gain(0.7); // 70% dry

    // Create effects
    this.reverb = new Tone.Reverb({
      decay: 2.5, // 2.5 second decay
      wet: 1.0, // 100% wet (send/return style)
    });

    this.delay = new Tone.FeedbackDelay({
      delayTime: '8n', // 8th note delay (tempo-synced)
      feedback: 0.3, // 30% feedback
      wet: 1.0, // 100% wet
    });

    this.limiter = new Tone.Limiter(-1); // -1dB ceiling (prevent clipping)

    // Create effect sends
    this.reverbSend = new Tone.Gain(0.2); // 20% reverb
    this.delaySend = new Tone.Gain(0.15); // 15% delay

    // Saturation mix gain control
    this.saturationMix = new Tone.Gain(0.0); // Default: no saturation

    // Signal routing:
    // Input → Compressor → PostCompressor → [Saturation Mix] → Dry/FX sends → Limiter → Destination
    
    // Main chain: Input → Compressor
    this.input.connect(this.compressor);
    this.compressor.connect(this.postCompressor);

    // Saturation parallel path (dry/wet mix)
    this.postCompressor.connect(this.saturationDry);
    this.postCompressor.connect(this.saturation);
    this.saturation.connect(this.saturationWet);
    this.saturationDry.connect(this.saturationMerge);
    this.saturationWet.connect(this.saturationMerge);

    // From saturation merge → effects sends and dry
    this.saturationMerge.connect(this.dryGain);
    this.dryGain.connect(this.limiter);

    this.saturationMerge.connect(this.reverbSend);
    this.reverbSend.connect(this.reverb);
    this.reverb.connect(this.limiter);

    this.saturationMerge.connect(this.delaySend);
    this.delaySend.connect(this.delay);
    this.delay.connect(this.limiter);

    this.limiter.toDestination();
  }

  /**
   * Set reverb amount (0-1)
   */
  setReverbAmount(amount: number): void {
    this.reverbSend.gain.rampTo(amount, 0.1);
  }

  /**
   * Set delay amount (0-1)
   */
  setDelayAmount(amount: number): void {
    this.delaySend.gain.rampTo(amount, 0.1);
  }

  /**
   * Set delay time (rhythm-based)
   */
  setDelayTime(time: Tone.Unit.Time): void {
    this.delay.delayTime.rampTo(time, 0.1);
  }

  /**
   * Set delay feedback (0-1)
   */
  setDelayFeedback(feedback: number): void {
    this.delay.feedback.rampTo(feedback, 0.1);
  }

  /**
   * Set reverb decay time (seconds)
   */
  setReverbDecay(decay: number): void {
    this.reverb.decay = decay;
  }

  /**
   * Set compression amount (0-1)
   * 0 = light compression, 1 = heavy compression
   */
  setCompressionAmount(amount: number): void {
    // Map 0-1 to compression parameters
    // Threshold: -6dB (light) to -36dB (heavy)
    const threshold = -6 - (amount * 30);
    // Ratio: 2:1 (light) to 12:1 (heavy)
    const ratio = 2 + (amount * 10);
    // Makeup gain to compensate for compression
    const makeupGain = 1 + (amount * 0.5);
    
    this.compressor.threshold.rampTo(threshold, 0.1);
    this.compressor.ratio.rampTo(ratio, 0.1);
    this.postCompressor.gain.rampTo(makeupGain, 0.1);
  }

  /**
   * Set saturation amount (0-1)
   * 0 = clean, 1 = heavily saturated
   */
  setSaturationAmount(amount: number): void {
    // Blend between dry and saturated signal
    // At 0: 100% dry, 0% wet
    // At 1: 0% dry, 100% wet
    const wetGain = amount;
    const dryGain = 1 - (amount * 0.5); // Keep some dry signal even at max
    
    this.saturationWet.gain.rampTo(wetGain, 0.1);
    this.saturationDry.gain.rampTo(dryGain, 0.1);
    
    // Also increase distortion amount with saturation
    // Map 0-1 to 0.1-0.6 distortion range
    const distortion = 0.1 + (amount * 0.5);
    this.saturation.distortion = distortion;
  }

  /**
   * Set saturation drive (intensity of harmonics)
   * @param drive 0-1 (0 = warm, 1 = aggressive)
   */
  setSaturationDrive(drive: number): void {
    // Map 0-1 to distortion range 0.05-0.8
    const distortion = 0.05 + (drive * 0.75);
    this.saturation.distortion = distortion;
  }

  /**
   * Set master output volume
   */
  setMasterVolume(volume: number): void {
    this.dryGain.gain.rampTo(volume, 0.1);
  }

  /**
   * Get master output node for recording
   */
  getMasterOutput(): Tone.Limiter {
    return this.limiter;
  }

  /**
   * Dispose of all effects (cleanup)
   */
  dispose(): void {
    this.reverb.dispose();
    this.delay.dispose();
    this.compressor.dispose();
    this.saturation.dispose();
    this.saturationMix.dispose();
    this.saturationDry.dispose();
    this.saturationWet.dispose();
    this.saturationMerge.dispose();
    this.postCompressor.dispose();
    this.limiter.dispose();
    this.dryGain.dispose();
    this.reverbSend.dispose();
    this.delaySend.dispose();
    this.input.dispose();
  }
}
